import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
} from "reactstrap";
import { useCallback, useEffect, useState } from "react";
import { Formik, useFormik } from "formik";
import * as Yup from "yup";
import { getLoginUserDetails } from "services/_user";
import { updateLoginUserDataById } from "services/_user";
import { successToast } from "components/toast";
import { errorToast } from "components/toast";
import Header from "components/Headers/Header";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { updateLoginUserPassword } from "services/_user";

const ProfileSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .required("Email is required")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format"),
  phone_no: Yup.string()
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
    .optional(),
});

const Profile = () => {
  const [loginUserData, setLoginUserData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [open, setOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: loginUserData?.name || "",
      email: loginUserData?.email || "",
      phone_no: loginUserData?.phone_no || "",
    },
    validationSchema: ProfileSchema,
    onSubmit: (values) => {
      updateDetails(values);
      setIsEditing(false);
    },
  });

  const initialData = useCallback(async () => {
    const data = await getLoginUserDetails();
    if (data.isSuccess) {
      setLoginUserData(data.data);
      formik.setValues({
        name: data.data.name || "",
        email: data.data.email || "",
        phone_no: data.data.phone_no || "",
      });
    }
  }, [formik]);

  useEffect(() => {
    initialData();
  }, [initialData]);

  const updateDetails = async (values) => {
    const data = await updateLoginUserDataById(values);
    if (data.isSuccess) {
      successToast(data.message);
    } else {
      errorToast(data.message);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const validationSchemaPassWord = Yup.object().shape({
    newPassword: Yup.string()
      .required("New Password is required")
      .matches(
        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/,
        "Password must be at least 8 characters, include 1 uppercase letter, 1 number, and 1 special character"
      ),
    oldPassword: Yup.string().required("Old Password is required"),
  });

  const handleSubmit = async (values) => {
    const data = await updateLoginUserPassword(values);
    if (data.isSuccess) {
      successToast(data.message);
      setOpen(false);
    } else {
      errorToast(data.message);
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">My account</h3>
                  </Col>
                  <Col className="text-right" xs="4">
                    {!isEditing ? (
                      <Button
                        color="primary"
                        size="md"
                        onClick={() => setIsEditing(true)}
                      >
                        Edit Profile
                      </Button>
                    ) : (
                      <Button
                        color="success"
                        size="md"
                        onClick={formik.handleSubmit}
                      >
                        Save
                      </Button>
                    )}
                    <Button
                      color="primary"
                      size="md"
                      onClick={() => setOpen(true)}
                    >
                      Update Password
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form onSubmit={formik.handleSubmit}>
                  <h6 className="heading-small text-muted mb-4">
                    User information
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-name"
                          >
                            Name <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-name"
                            name="name"
                            type="text"
                            placeholder="Enter name"
                            disabled={!isEditing}
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.name && formik.errors.name && (
                            <small className="text-danger">
                              {formik.errors.name}
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-email"
                          >
                            Email <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-email"
                            name="email"
                            type="email"
                            placeholder="Enter email"
                            disabled={!isEditing}
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.email && formik.errors.email && (
                            <small className="text-danger">
                              {formik.errors.email}
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-phone"
                          >
                            Phone <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-phone"
                            name="phone_no"
                            type="text"
                            placeholder="Enter phone"
                            disabled={!isEditing}
                            value={formik.values.phone_no}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {formik.touched.phone_no &&
                            formik.errors.phone_no && (
                              <small className="text-danger">
                                {formik.errors.phone_no}
                              </small>
                            )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>Update Password</DialogTitle>
          <Formik
            initialValues={{ newPassword: "", oldPassword: "" }}
            validationSchema={validationSchemaPassWord}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              handleChange,
              handleBlur,
              touched,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form onSubmit={handleSubmit} id="subscription-form">
                <DialogContent>
                  <TextField
                    required
                    margin="dense"
                    id="oldPassword"
                    name="oldPassword"
                    label="Old Password"
                    type="password"
                    fullWidth
                    variant="standard"
                    value={values.oldPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.oldPassword && Boolean(errors.oldPassword)}
                    helperText={touched.oldPassword && errors.oldPassword}
                  />
                  <TextField
                    required
                    margin="dense"
                    id="newPassword"
                    name="newPassword"
                    label="New Password"
                    type="password"
                    fullWidth
                    variant="standard"
                    value={values.newPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.newPassword && Boolean(errors.newPassword)}
                    helperText={touched.newPassword && errors.newPassword}
                  />
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleClose}
                    style={{
                      width: "100%",
                      backgroundColor: "red",
                      color: "white",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    style={{
                      width: "100%",
                      color: "white",
                      backgroundColor: "green",
                    }}
                  >
                    Save
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </Dialog>
      </Container>
    </>
  );
};

export default Profile;
